import React, { useMemo, useState } from 'react';
import { Row, Col, Spinner, Button } from 'react-bootstrap';
import { FaUsers, FaHeadset, FaRobot, FaUserTie, FaTimesCircle, FaChartBar, FaFileExcel } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import CardKPI from '~/Components/RelatorioLeadsVendas/CardKPI';
import { consultarIndicadoresLeadsAnalitico } from '~/Services/Service';
import { salvarEmExcel } from '~/Utils/Helpers';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const CORES_GRAFICOS = [
  'rgba(54, 162, 235, 0.8)',
  'rgba(255, 99, 132, 0.8)',
  'rgba(255, 206, 86, 0.8)',
  'rgba(75, 192, 192, 0.8)',
  'rgba(153, 102, 255, 0.8)',
  'rgba(255, 159, 64, 0.8)',
  'rgba(199, 199, 199, 0.8)',
  'rgba(83, 102, 255, 0.8)',
  'rgba(255, 99, 255, 0.8)',
  'rgba(99, 255, 132, 0.8)',
];

const TabIndicadoresLeads = ({ dados, loading, montarFiltros }) => {
  const [isLoadingExcel, setIsLoadingExcel] = useState(false);

  const handleExportarExcel = async () => {
    try {
      setIsLoadingExcel(true);
      const filtros = montarFiltros();
      const dadosAnaliticos = await consultarIndicadoresLeadsAnalitico(filtros);

      const dadosExcel = dadosAnaliticos.map(item => ({
        codigo_prospecto: item.codigo_prospecto,
        codigo_familia: item.codigo_familia,
        data_cadastro: item.data_cadastro,
        nome_empreendimento: item.nome_empreendimento,
        nome_imobiliaria: item.nome_imobiliaria,
        nome_atendente: item.nome_atendente,
        descricao_origem: item.descricao_origem,
        descricao_midia: item.descricao_midia,
        descricao_status: item.descricao_status,
        descricao_motivo_descarte: item.descricao_motivo_descarte,
        descricao_etapa: item.descricao_etapa,
        descartado: item.is_descartado ? 'Sim' : 'Não',
        cav: item.is_cav ? 'Sim' : 'Não',
        stella: item.is_stella ? 'Sim' : 'Não',
        atribuido_corretor: item.is_atribuido_corretor ? 'Sim' : 'Não',
      }));

      await salvarEmExcel(dadosExcel, '/exportador/excel/relatorio-leads-vendas/leads');
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
    } finally {
      setIsLoadingExcel(false);
    }
  };

  const kpis = useMemo(() => {
    if (!dados) return null;
    return {
      leadsRecebidos: dados.leads_recebidos || 0,
      passouPelaCav: dados.passou_pela_cav || 0,
      passouPelaStella: dados.passou_pela_stella || 0,
      atribuidoCorretor: dados.atribuido_corretor || 0,
      descartados: dados.descartados || 0,
    };
  }, [dados]);

  const criarDadosPizza = (lista, label) => {
    if (!lista || !lista.length) return null;
    const total = lista.reduce((acc, item) => acc + (item.valor || 0), 0);
    return {
      labels: lista.map(item => item.label || 'N/I'),
      datasets: [{
        label,
        data: lista.map(item => item.valor || 0),
        backgroundColor: CORES_GRAFICOS.slice(0, lista.length),
        borderWidth: 1,
      }],
      _total: total,
    };
  };

  const criarDadosBarras = (lista, label, horizontal = false) => {
    if (!lista || !lista.length) return null;
    return {
      labels: lista.map(item => item.label || 'N/I'),
      datasets: [{
        label,
        data: lista.map(item => item.valor || 0),
        backgroundColor: 'rgba(54, 162, 235, 0.7)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      }],
    };
  };

  const optionsPizza = (total) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed || 0;
            const perc = total > 0 ? ((value / total) * 100).toFixed(1) + '%' : '0%';
            return `${context.label}: ${value.toLocaleString('pt-BR')} (${perc})`;
          },
        },
      },
    },
  });

  const optionsBarrasVertical = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.parsed.y.toLocaleString('pt-BR')}`,
        },
      },
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const optionsBarrasHorizontal = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.parsed.x.toLocaleString('pt-BR')}`,
        },
      },
    },
    scales: {
      x: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const dadosLeadsPorImobiliaria = useMemo(function() {
    return criarDadosPizza(dados && dados.leads_por_imobiliaria, 'Leads por Imobiliária');
  }, [dados]);

  const dadosLeadsPorAtendente = useMemo(function() {
    return criarDadosBarras(dados && dados.leads_por_atendente_cav, 'Leads');
  }, [dados]);

  const dadosMotivosDescarte = useMemo(function() {
    return criarDadosBarras(dados && dados.motivos_descarte, 'Descartados');
  }, [dados]);

  const dadosDescartadosPorEmpreendimento = useMemo(function() {
    return criarDadosBarras(dados && dados.descartados_por_empreendimento, 'Descartados');
  }, [dados]);

  const dadosLeadsPorOrigem = useMemo(function() {
    return criarDadosPizza(dados && dados.leads_por_origem, 'Leads por Origem');
  }, [dados]);

  const dadosTopMidias = useMemo(function() {
    return criarDadosBarras(dados && dados.top_midias_entrada, 'Leads');
  }, [dados]);

  if (loading) {
    return (
      <div className="loading-overlay">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="tab-indicadores-leads">
      <div className="d-flex justify-content-end mb-3">
        <Button
          variant="success"
          disabled={isLoadingExcel || !dados}
          onClick={handleExportarExcel}
        >
          {isLoadingExcel ? (
            <Spinner animation="border" size="sm" className="mr-1" />
          ) : (
            <FaFileExcel className="mr-1" />
          )}
          {isLoadingExcel ? 'Exportando...' : 'Exportar Excel'}
        </Button>
      </div>
      <Row className="kpis-container g-3 mb-4">
        <Col xs={12} sm={6} md={4} lg>
          <CardKPI
            titulo="Leads Recebidos"
            valor={kpis ? kpis.leadsRecebidos : 0}
            cor="#007bff"
            icone={<FaUsers />}
            loading={loading}
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg>
          <CardKPI
            titulo="Passou pela CAV"
            valor={kpis ? kpis.passouPelaCav : 0}
            cor="#28a745"
            icone={<FaHeadset />}
            loading={loading}
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg>
          <CardKPI
            titulo="Passou pela Stella"
            valor={kpis ? kpis.passouPelaStella : 0}
            cor="#6f42c1"
            icone={<FaRobot />}
            loading={loading}
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg>
          <CardKPI
            titulo="Atribuído Corretor"
            valor={kpis ? kpis.atribuidoCorretor : 0}
            cor="#17a2b8"
            icone={<FaUserTie />}
            loading={loading}
          />
        </Col>
        <Col xs={12} sm={6} md={4} lg>
          <CardKPI
            titulo="Descartados"
            valor={kpis ? kpis.descartados : 0}
            cor="#dc3545"
            icone={<FaTimesCircle />}
            loading={loading}
          />
        </Col>
      </Row>

      <div className="graficos-container">
        <Row className="g-3 mb-3">
          <Col xs={12} lg={6}>
            <div className="grafico-card">
              <h6 className="grafico-titulo">Total de Leads por Imobiliária</h6>
              <div className="grafico-wrapper" style={{ height: 300 }}>
                {dadosLeadsPorImobiliaria ? (
                  <Pie data={dadosLeadsPorImobiliaria} options={optionsPizza(dadosLeadsPorImobiliaria._total)} />
                ) : (
                  <div className="empty-state">
                    <FaChartBar className="empty-icon" />
                    <span className="empty-text">Sem dados para exibir</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
          <Col xs={12} lg={6}>
            <div className="grafico-card">
              <h6 className="grafico-titulo">Top Leads Atendidos por Atendentes (CAV)</h6>
              <div className="grafico-wrapper" style={{ height: 300 }}>
                {dadosLeadsPorAtendente ? (
                  <Bar data={dadosLeadsPorAtendente} options={optionsBarrasVertical} />
                ) : (
                  <div className="empty-state">
                    <FaChartBar className="empty-icon" />
                    <span className="empty-text">Sem dados para exibir</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xs={12} lg={6}>
            <div className="grafico-card">
              <h6 className="grafico-titulo">Top Motivos de Cancelamento/Descarte</h6>
              <div className="grafico-wrapper" style={{ height: 300 }}>
                {dadosMotivosDescarte ? (
                  <Bar data={dadosMotivosDescarte} options={optionsBarrasVertical} />
                ) : (
                  <div className="empty-state">
                    <FaChartBar className="empty-icon" />
                    <span className="empty-text">Sem dados para exibir</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
          <Col xs={12} lg={6}>
            <div className="grafico-card">
              <h6 className="grafico-titulo">Descartados por Empreendimento (Top 10)</h6>
              <div className="grafico-wrapper" style={{ height: 300 }}>
                {dadosDescartadosPorEmpreendimento ? (
                  <Bar data={dadosDescartadosPorEmpreendimento} options={optionsBarrasHorizontal} />
                ) : (
                  <div className="empty-state">
                    <FaChartBar className="empty-icon" />
                    <span className="empty-text">Sem dados para exibir</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col xs={12} lg={6}>
            <div className="grafico-card">
              <h6 className="grafico-titulo">Leads por Origem</h6>
              <div className="grafico-wrapper" style={{ height: 300 }}>
                {dadosLeadsPorOrigem ? (
                  <Pie data={dadosLeadsPorOrigem} options={optionsPizza(dadosLeadsPorOrigem._total)} />
                ) : (
                  <div className="empty-state">
                    <FaChartBar className="empty-icon" />
                    <span className="empty-text">Sem dados para exibir</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
          <Col xs={12} lg={6}>
            <div className="grafico-card">
              <h6 className="grafico-titulo">Top Mídias de Entrada (Top 10)</h6>
              <div className="grafico-wrapper" style={{ height: 300 }}>
                {dadosTopMidias ? (
                  <Bar data={dadosTopMidias} options={optionsBarrasHorizontal} />
                ) : (
                  <div className="empty-state">
                    <FaChartBar className="empty-icon" />
                    <span className="empty-text">Sem dados para exibir</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default TabIndicadoresLeads;
